import { useMemo } from 'react';
import { useGameState } from '../GameProvider';
import Timer from './Timer';
import { useTimer } from '../hooks/useTimer';
import './DealerControls.css';

export default function DealerControls({
  onReady,
  onDeal,
  onBetweenRoundsReady,
}) {
  const state = useGameState();
  const {
    uid, phase, players, dealerUid,
    readyStatus, betweenRoundsReady, betweenRoundsTimerStart,
  } = state;

  const isDealer = uid === dealerUid;
  const isLobbyOrResults = phase === 'lobby' || phase === 'results';

  const activePlayerUids = useMemo(() =>
    Object.entries(players)
      .filter(([, p]) => !p.sittingOut && !p.kicked)
      .map(([id]) => id),
    [players]
  );

  // Between-rounds ready (used in lobby and results phase)
  const readyMap = phase === 'results' ? betweenRoundsReady : readyStatus;
  const amReady = !!readyMap[uid];
  const readyPlayerCount = activePlayerUids.filter(id => readyMap[id]).length;
  const allReady = activePlayerUids.length >= 2 && activePlayerUids.every(id => readyMap[id]);
  const waitingFor = activePlayerUids.filter(id => !readyMap[id]);

  // Dealer ready = show Deal button. Deal requires at least 2 readied players.
  const dealerReady = !!readyMap[dealerUid];
  const showDealButton = isDealer && (dealerReady || allReady);
  const canDeal = readyPlayerCount >= 2;

  // Between rounds timer (60s, only on subsequent hands)
  const isSubsequentHand = phase === 'results';
  const { remaining: betweenRemaining } = useTimer(
    isSubsequentHand ? betweenRoundsTimerStart : null,
    60,
    null // onExpire handled by gameService
  );

  const handleReady = () => {
    if (phase === 'results') {
      onBetweenRoundsReady(!amReady);
    } else {
      onReady(!amReady);
    }
  };

  if (!isLobbyOrResults) return null;

  return (
    <div className="dealer-controls">
      {/* Timer (subsequent hands only) */}
      {isSubsequentHand && betweenRoundsTimerStart && (
        <Timer remaining={betweenRemaining} warning={15} danger={5} />
      )}

      {/* Deal button (dealer only, after readying) */}
      {showDealButton ? (
        <button
          className="dealer-controls__deal-btn"
          onClick={onDeal}
          disabled={!canDeal}
        >
          Deal
        </button>
      ) : (
        /* Ready button for all players */
        <button
          className={`dealer-controls__ready-btn ${amReady ? 'dealer-controls__ready-btn--active' : ''}`}
          onClick={handleReady}
        >
          {amReady ? 'Ready ✓' : 'Ready'}
        </button>
      )}

      {/* Waiting for list */}
      {waitingFor.length > 0 && (
        <div className="dealer-controls__waiting">
          <span className="dealer-controls__waiting-label">Waiting for:</span>
          {waitingFor.map(id => (
            <span key={id} className="dealer-controls__waiting-name">
              {players[id]?.displayName || 'Unknown'}
            </span>
          ))}
        </div>
      )}

      {showDealButton && !canDeal && (
        <p className="dealer-controls__hint">Need at least 2 players ready</p>
      )}
    </div>
  );
}
